import { API } from '../Auth';
import { kindForSource } from 'AssetManager/LandscapeUtils';

const sampleLandscapeHomeSummary = {
  generatedAt: '2026-03-26T19:12:34.000Z',
  totalSources: 7,
  categories: ['RELATIONAL', 'FILE_FOLDER', 'NOSQL'],
  sources: [
    {
      id: 101,
      name: 'prod-orders-rds',
      datasourceType: 'RELATIONAL',
      engine: 'postgresql',
      region: 'us-west-2',
      host: 'prod-orders.cluster-xyz.us-west-2.rds.amazonaws.com',
      port: 5432,
      database: 'orders',
      user: '',
      latestDiscoveryAt: '2026-03-25T15:08:00.000Z',
      latestDiscoveryId: '18422',
      latestRiskScore: 5,
      latestRiskLevel: 'HIGH',
      latestFindings: [{ entity: 'EMAIL' }, { entity: 'CREDIT_CARD' }],
    },
    {
      id: 102,
      name: 'finance-s3',
      datasourceType: 'FILE_FOLDER',
      engine: 's3',
      region: 'us-west-2',
      host: 's3://finance-sensitive-bucket',
      port: -1,
      database: '',
      user: '',
      latestDiscoveryAt: null,
      latestDiscoveryId: null,
      latestRiskScore: null,
      latestRiskLevel: 'UNKNOWN',
    },
    {
      id: 103,
      name: 'events-mongo',
      datasourceType: 'NOSQL',
      engine: 'mongodb',
      region: 'us-east-1',
      host: 'events-mongo.internal',
      port: 27017,
      database: 'events',
      user: '',
      latestDiscoveryAt: '2026-03-24T13:05:00.000Z',
      latestDiscoveryId: '18380',
      latestRiskScore: 3,
      latestRiskLevel: 'MEDIUM',
    },
    {
      id: 104,
      name: 'hr-rds',
      datasourceType: 'RELATIONAL',
      engine: 'mysql',
      region: 'us-west-1',
      host: 'hr-prod.cluster-abc.us-west-1.rds.amazonaws.com',
      port: 3306,
      database: 'hr',
      user: '',
      latestDiscoveryAt: '2026-03-20T18:00:00.000Z',
      latestDiscoveryId: '18211',
      latestRiskScore: 6,
      latestRiskLevel: 'CRITICAL',
    },
    {
      id: 105,
      name: 'exports-sftp',
      datasourceType: 'FILE_FOLDER',
      engine: 'sftp',
      region: 'us-east-2',
      host: 'sftp://exports.example.net',
      port: 22,
      database: '',
      user: '',
      latestDiscoveryAt: null,
      latestDiscoveryId: null,
      latestRiskScore: null,
      latestRiskLevel: 'UNKNOWN',
    },
    {
      id: 106,
      name: 'snowflake-analytics',
      datasourceType: 'RELATIONAL',
      engine: 'snowflake',
      region: 'external',
      host: 'xy12345.us-east-1.snowflakecomputing.com',
      port: 443,
      database: 'ANALYTICS',
      user: '',
      latestDiscoveryAt: '2026-03-22T09:11:00.000Z',
      latestDiscoveryId: '18279',
      latestRiskScore: 4,
      latestRiskLevel: 'MEDIUM_HIGH',
    },
    {
      id: 107,
      name: 'customer-data-lake',
      datasourceType: 'FILE_FOLDER',
      engine: 's3',
      region: 'us-west-2',
      host: 's3://customer-data-lake',
      port: -1,
      database: '',
      user: '',
      latestDiscoveryAt: '2026-03-18T11:42:00.000Z',
      latestDiscoveryId: '18155',
      latestRiskScore: 2,
      latestRiskLevel: 'MED_LOW',
    },
  ],
};

/**
 * The source's effective `landscape` tag as the risk overview reports it. `none` = platform-owned
 * (a TDM staging source or raw view) or hidden by a user: listed everywhere else, but not on the
 * Data Privacy Workflow or the Enterprise Data Landscape. The overview itself keeps every source
 * because other pages read it; the workflow page applies this.
 */
export const isOnWorkflow = (source) => Boolean(source) && String(source.landscape || 'both').toLowerCase() !== 'none';

const normalizeSource = (source) => ({
  id: source.id,
  name: source.name,
  landscape: String(source.landscape || 'both').toLowerCase(),
  datasourceType: source.datasourceType || 'RELATIONAL',
  engine: source.engine || 'jdbc',
  latestDiscoveryAt: source.latestDiscoveryAt || null,
  latestDiscoveryId: source.latestDiscoveryId || null,
  // The platform's 0–100 score, as sent. (It used to be clamped into 1–6 here — a leftover of the
  // retired entity-weight scale — so every discovered source read "risk score 6". Panels that
  // still index the legacy 1–6 bands derive them from the LEVEL via riskTier.legacyBandOf.)
  latestRiskScore: typeof source.latestRiskScore === 'number' ? source.latestRiskScore : null,
  latestRiskLevel: normalizeRiskLevel(source.latestRiskLevel),
  latestFindings: Array.isArray(source.latestFindings) ? source.latestFindings : [],
  region: source.region || '',
  host: source.host || '',
  port: source.port != null ? source.port : -1,
  database: source.database || '',
  user: source.user || '',
});

function normalizeRiskLevel(level) {
  const allowed = new Set(['LOW', 'MED_LOW', 'MEDIUM', 'MEDIUM_HIGH', 'HIGH', 'CRITICAL', 'UNKNOWN']);
  if (!level || typeof level !== 'string') return 'UNKNOWN';
  const upper = level.toUpperCase();
  if (allowed.has(upper)) return upper;
  if (upper === 'VERY_HIGH') return 'HIGH';
  if (upper === 'PHI') return 'CRITICAL';
  return 'UNKNOWN';
}

const normalizeHomeSummary = (payload) => {
  const sources = Array.isArray(payload?.sources) ? payload.sources.map(normalizeSource) : [];
  return {
    generatedAt: payload?.generatedAt || new Date().toISOString(),
    totalSources: payload?.totalSources != null ? payload.totalSources : sources.length,
    categories: Array.isArray(payload?.categories) ? payload.categories : ['RELATIONAL', 'FILE_FOLDER', 'NOSQL'],
    sources,
  };
};

// Install-wide: every data source, on both landscapes.
const getLandscapeHomeSummary = async () => {
  try {
    const response = await API.get('discoverRuns', '/api/v1/discover/jobs/risk-overview');
    const payload = response?.data ? response.data : response;
    return normalizeHomeSummary(payload);
  } catch (e) {
    console.log('Failed to load /api/v1/discover/jobs/risk-overview, using sample payload', e);
    return normalizeHomeSummary(sampleLandscapeHomeSummary);
  }
};

// Infer the cloud a source lives on from its host/endpoint. The risk-overview payload
// doesn't carry a `cloud` field, so we read it off the well-known hostname suffixes each
// provider stamps on managed-DB and object-store endpoints. Returns null for on-prem /
// unrecognized hosts so callers can fall back to a generic (non-branded) source key.
const inferCloudFromHost = (host) => {
  const h = String(host || '').toLowerCase();
  if (!h) return null;
  if (
    h.includes('.database.windows.net') ||      // Azure SQL / SQL MI / Synapse
    h.includes('.postgres.database.azure.com') ||
    h.includes('.mysql.database.azure.com') ||
    h.includes('.database.azure.com') ||
    h.includes('azblob://') ||
    h.includes('.blob.core.windows.net')
  ) return 'azure';
  if (
    h.includes('.rds.amazonaws.com') ||
    h.includes('.redshift.amazonaws.com') ||
    h.startsWith('s3://') ||
    h.includes('.s3.amazonaws.com')
  ) return 'aws';
  if (
    h.includes('.gcp.') ||
    h.endsWith('.googleapis.com') ||
    h.includes('cloudsql') ||
    h.startsWith('gs://') ||
    h.includes('.storage.googleapis.com')
  ) return 'gcp';
  return null;
};

// Azure exposes a per-engine managed-DB family; map the discover engine onto the matching
// `azure_*` source key so the SOURCE column renders the right icon + label. Engines arrive
// already normalized to connection types (postgresql / mssql / mysql / oracledb / mariadb).
const azureSourceForEngine = (engine) => {
  const e = String(engine || '').toLowerCase();
  if (e.includes('postgres')) return 'azure_postgres';
  if (e === 'mssql' || e.includes('sqlserver')) return 'azure_sql';
  if (e.includes('mysql') || e.includes('mariadb')) return 'azure_mysql';
  if (e.includes('oracle')) return 'azure_oracle';
  return 'azure_sql';
};

// Resolve the legacy `source` key used by the SOURCE-icon column and the home-page tile
// filters. Cloud-aware: an Azure-hosted relational DB no longer collapses to the AWS "RDS"
// badge. Falls back to a generic key for on-prem / unrecognized hosts rather than mislabeling.
const toLegacySource = (datasourceType, host, engine) => {
  const cloud = inferCloudFromHost(host);

  if (datasourceType && datasourceType.startsWith('FILE_')) {
    if (cloud === 'azure') return 'azure_storage';
    if (cloud === 'gcp') return 'gcs';
    return 's3';
  }
  if (datasourceType === 'NOSQL') return 'external';

  // RELATIONAL (and anything else carrying a DB engine).
  if (cloud === 'azure') return azureSourceForEngine(engine);
  if (cloud === 'gcp') return 'gcp_sql';
  if (cloud === 'aws') return 'rds';
  // On-prem / unknown host: don't claim AWS RDS. `external` renders a generic server icon.
  return 'external';
};

const mapSummaryToUnknownSources = (summary, region = 'all') => {
  const sources = Array.isArray(summary?.sources) ? summary.sources : [];
  const undiscovered = sources
    .filter((s) => s.latestDiscoveryAt == null)
    .map((s) => ({
      id: s.id,
      name: s.name,
      datasourceType: s.datasourceType || '',
      source: toLegacySource(s.datasourceType, s.host || s.name, s.engine),
      engine: s.engine,
      region: s.region || '',
      host: s.host || s.name,
      port: s.port != null ? s.port : -1,
      database: s.database || '',
      user: s.user || '',
      password: '',
      connections: s.connectionName ? [s.connectionName] : [],
      createTime: Date.now(),
    }));

  // Legacy home-page tile filters map onto datasource categories, not a single cloud's source
  // key, so a tile (e.g. "RDS") surfaces relational sources across AWS/Azure/GCP and on-prem.
  // Keyed off `datasourceType` (unambiguous) rather than the cloud-specific `source`.
  if (region === 'ec2') return undiscovered.filter((o) => kindForSource(o.source) === 'servers');
  if (region === 'rds') return undiscovered.filter((o) => o.datasourceType === 'RELATIONAL');
  if (region === 's3') return undiscovered.filter((o) => String(o.datasourceType).startsWith('FILE_'));
  if (region === 'snowflake') return undiscovered.filter((o) => o.engine === 'snowflake');
  if (region === 'mongodb') return undiscovered.filter((o) => o.engine === 'mongodb');
  if (region !== 'all') return undiscovered.filter((o) => o.region === region);
  return undiscovered;
};

const mapSummaryToRiskRows = (summary, sourceParam) => {
  const sources = Array.isArray(summary?.sources) ? summary.sources : [];
  const sourceParamValue = (sourceParam || '').toLowerCase();
  const filtered = sources.filter((s) => {
    const hasDiscovery = s.latestDiscoveryAt != null || s.latestDiscoveryId != null;
    if (!hasDiscovery) return false;
    if (sourceParamValue === 'rds') return s.datasourceType === 'RELATIONAL';
    if (sourceParamValue === 's3') return s.datasourceType && s.datasourceType.startsWith('FILE_');
    if (sourceParamValue === 'external') return s.datasourceType === 'NOSQL';
    if (sourceParamValue === 'relational') return s.datasourceType === 'RELATIONAL';
    if (sourceParamValue === 'file_folder') return s.datasourceType && s.datasourceType.startsWith('FILE_');
    if (sourceParamValue === 'nosql') return s.datasourceType === 'NOSQL';
    return true;
  });

  return filtered
    .map((s) => ({
      id: s.id,
      name: s.name,
      source: toLegacySource(s.datasourceType, s.host || s.name, s.engine),
      engine: s.engine,
      region: s.region || '',
      connections: [s.name],
      findings: s.latestFindings || [],
      latestRiskScore: s.latestRiskScore,
      latestDiscoveryAt: s.latestDiscoveryAt,
      latestDiscoveryId: s.latestDiscoveryId,
    }))
    .sort((a, b) => {
      const scoreA = typeof a.latestRiskScore === 'number' ? a.latestRiskScore : 1;
      const scoreB = typeof b.latestRiskScore === 'number' ? b.latestRiskScore : 1;
      return scoreB - scoreA;
    });
};

export {
  getLandscapeHomeSummary,
  normalizeHomeSummary,
  mapSummaryToUnknownSources,
  mapSummaryToRiskRows,
};
